import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { CourseStatus } from '../../../../../api/types/programs/course/course-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

// Match the structure of AbstractCourse from the store
interface Course {
  id: string | number;
  courseName: string;
  [key: string]: any; // Allow for additional properties
}

interface CourseFinancialBranchBreakdownFilterProps {
  onSearch: (filters: CourseFinancialBranchBreakdownFilterDatamodel) => void;
}

export interface CourseFinancialBranchBreakdownFilterDatamodel {
  branchId: number;
  programId: number | null;
  years: number[];
  courseId: number | null;
  courseStatus: CourseStatus | null;
}

export const CourseFinancialBranchBreakdownFilter = observer(
  ({ onSearch }: CourseFinancialBranchBreakdownFilterProps) => {
    const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
    const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
    const [courseStatus, setCourseStatus] = useState<CourseStatus | null>(null);
    const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
    const [branchSearchTerm, setBranchSearchTerm] = useState('');
    const [programSearchTerm, setProgramSearchTerm] = useState('');

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const courseStatuses = [
      { label: 'פעיל', value: 'active' },
      { label: 'מושהה', value: 'inactive' },
      { label: 'בהמתנה', value: 'pending' },
    ];

    useEffect(() => {
      // Update available courses when program is selected
      if (selectedProgram) {
        const program = globalSettingsStores.abstractProgram.find(
          (p) => p.id === selectedProgram,
        );
        if (program && program.courses) {
          setAvailableCourses(program.courses);
        } else {
          setAvailableCourses([]);
        }
      } else {
        setAvailableCourses([]);
        setSelectedCourse(null);
      }
    }, [selectedProgram]);

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranch(branchId);
    };

    const handleProgramToggle = (programId: number) => {
      setSelectedProgram(programId);
      setSelectedCourse(null);
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleSearchClick = () => {
      const filters: CourseFinancialBranchBreakdownFilterDatamodel = {
        branchId: selectedBranch,
        programId: selectedProgram,
        years: selectedYears,
        courseId: selectedCourse,
        courseStatus: courseStatus,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranch(null);
      setSelectedProgram(null);
      setSelectedYears([]);
      setSelectedCourse(null);
      setCourseStatus(null);
      setAvailableCourses([]);
      setBranchSearchTerm('');
      setProgramSearchTerm('');
    };

    // Filter branches based on search term
    const filteredBranches = globalSettingsBranches.branches.filter((branch) =>
      branch.branchName.toLowerCase().includes(branchSearchTerm.toLowerCase()),
    );

    // Filter programs based on search term
    const filteredPrograms = globalSettingsStores.abstractProgram.filter(
      (program) =>
        program.programName
          .toLowerCase()
          .includes(programSearchTerm.toLowerCase()),
    );

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      radioGroup: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      radioOption: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      buttonContainer: {
        marginTop: 'auto',
        paddingTop: '16px',
      },
      searchInput: {
        width: '100%',
        marginBottom: '8px',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <CustomExpander defaultExpanded={true}>
            <div>שלוחה</div>
            <div style={styles.radioGroup}>
              <InputText
                value={branchSearchTerm}
                onChange={(e) => setBranchSearchTerm(e.target.value)}
                placeholder="חפש שלוחה..."
                style={styles.searchInput}
              />
              {filteredBranches.map((branch) => (
                <div key={branch.id} style={styles.radioOption}>
                  <RadioButton
                    inputId={branch.id.toString()}
                    name="branch"
                    value={branch.id}
                    onChange={(e) => handleBranchToggle(Number(e.value))}
                    checked={selectedBranch === branch.id}
                  />
                  <label htmlFor={branch.id.toString()}>
                    {branch.branchName}
                  </label>
                </div>
              ))}
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>תכנית לימודים</div>
            <div style={styles.radioGroup}>
              <InputText
                value={programSearchTerm}
                onChange={(e) => setProgramSearchTerm(e.target.value)}
                placeholder="חפש תכנית..."
                style={styles.searchInput}
              />
              {filteredPrograms.map((program) => (
                <div key={program.id} style={styles.radioOption}>
                  <RadioButton
                    inputId={program.id.toString()}
                    name="program"
                    value={program.id}
                    onChange={(e) => handleProgramToggle(Number(e.value))}
                    checked={selectedProgram === program.id}
                  />
                  <label htmlFor={program.id.toString()}>
                    {program.programName}
                  </label>
                </div>
              ))}
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          {selectedProgram && (
            <CustomExpander>
              <div>שם קורס</div>
              <div style={styles.radioGroup}>
                {availableCourses.map((course) => (
                  <div key={course.id} style={styles.radioOption}>
                    <RadioButton
                      inputId={course.id.toString()}
                      name="course"
                      value={course.id}
                      onChange={(e) => setSelectedCourse(Number(e.value))}
                      checked={selectedCourse === course.id}
                    />
                    <label htmlFor={course.id.toString()}>
                      {course.courseName}
                    </label>
                  </div>
                ))}
              </div>
            </CustomExpander>
          )}

          <CustomExpander>
            <div>סטטוס קורס</div>
            <div style={styles.radioGroup}>
              {Object.values(CourseStatus)
                .filter((status) => typeof status === 'string')
                .map((status) => (
                  <div key={status} style={styles.radioOption}>
                    <RadioButton
                      inputId={status}
                      name="status"
                      value={status}
                      onChange={(e) => setCourseStatus(e.value as CourseStatus)}
                      checked={courseStatus === status}
                    />
                    <label htmlFor={status}>
                      {CourseStatus.getDisplayText(status as CourseStatus)}
                    </label>
                  </div>
                ))}
            </div>
          </CustomExpander>

          <div style={styles.buttonContainer}>
            <Button
              label="חפש"
              icon="pi pi-search"
              onClick={handleSearchClick}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>
    );
  },
);
